/**
 * 연금술사 — 강화 재료와 장비의 잠재력을 다룬다 (UNI-ALCHEMY §7).
 *
 * 고른 장비의 굴림·위험을 먼저 보고, 필요한 재료 전환은 따로 펼친다.
 * 대장장이와 겹치지 않는다 — 승급·머지·제작은 저쪽 일이다.
 *
 * ⚠ **수치를 손으로 안 적는다.** 비율·비용·품질 범위는 전부 서버와 같은 미러 상수에서 온다.
 *   오늘(2026-09-06) 궁극 비용이 바뀌었는데 화면 다섯 곳이 옛 수치를 안내하고 있었고,
 *   그중 하나는 **눌러도 아무 일이 안 일어나는 버튼**이었다. 손으로 적으면 그렇게 낡는다.
 */
import {
  BLESSED_SHARD_COST, BSTONE_KEY, MAX_PLUS, ROLL_QUALITY_MAX, ROLL_QUALITY_MIN,
  SHARD_KEY, ULTIMATE_BSTONES, ULTIMATE_CHANCE, blessedStoneCost, isUltimateEnhance,
} from '../../world/map.js';
import { gearRoll } from '../../world/gearnames.js';
import { GAMBLE_PRICE } from '../../world/gamble.js';
import { S, esc } from '../../core/state.js';
import { whyNot } from '../../world/places.js';
import { buyBtn, cell, eqName, eqTitle, gearGlyph, gearTone, lore, muted, npcHead, num, split, stock } from './common.js';

/**
 * 굴림 품질 — **화면이 식을 복사하지 않는다.** `gearRoll` 은 서버 `items.ts` 와 같은 해시를
 * 쓰는 클라 거울이고(`world/gearnames.js`), `check-mirror` 가 두 답이 같은지 대조한다.
 *
 * ⚠ 한때 이 표시를 뺐다 — `world/map.js` 만 보고 「거울에 없다」고 판단했기 때문이다.
 *   **거울은 파일 하나가 아니다.** 없다고 말하기 전에 있을 법한 자리를 전부 열어야 한다.
 */
const quality = (e) => gearRoll(e.slot, e.id, e.rolled)?.q ?? null;

/** 이 패널이 다루는 장비 — 굴림이 있는 것. 조잡·고정 장비는 연금술사가 할 일이 없다. */
const rollable = (eq) => eq.filter((e) => !e.crude && e.rolled !== 0 && quality(e) !== null);

export function render({ y, at, eq, j }, { embedded = false } = {}) {
  const shards = stock(y, SHARD_KEY);
  const bstones = stock(y, BSTONE_KEY);
  const pool = rollable(eq);
  const picked = pool.find((e) => e.id === S.alchemySel) ?? null;

  let list = npcHead();
  let conversion = '';
  let ultimate = '';

  // ── ① 축복석 전환 ────────────────────────────────────────────
  {
    const can = Math.floor(shards / BLESSED_SHARD_COST);
    const why = whyNot(at, 'alchemyConvert');
    const lack = why ? [why] : can < 1 ? [`강화석 ${BLESSED_SHARD_COST - shards}개 부족`] : [];
    const count = Number(S.alchemyCount), need = count * BLESSED_SHARD_COST;
    const valid = /^\d+$/.test(S.alchemyCount) && Number.isSafeInteger(need) && Number.isSafeInteger(count) && count > 0;
    conversion = `<div class="sec"><h3>축복받은 강화석 전환 <em>강화석 ${BLESSED_SHARD_COST} → 축복받은 강화석 1</em></h3>
      <div class="kv"><span>보유</span><b>강화석 ${num(shards)} · 축복받은 강화석 ${num(bstones)}</b></div>
      <label class="row" for="alchemy-count">전환 수량 (개)
        <input id="alchemy-count" type="text" inputmode="numeric" pattern="[0-9]*" style="width:9em;max-width:100%" value="${esc(S.alchemyCount)}" oninput="act.alchemyCount(this.value)" ${S.alchemyBusy ? 'disabled' : ''}>
      </label>
      <div class="kv"><span>필요한 강화석</span><b>${valid ? `${num(need)}개` : '1 이상의 정수를 입력해 달라'}</b></div>
      <div class="row">${buyBtn(S.alchemyBusy ? '처리 중…' : '입력 수량 전환', 'act.alchemyConvert()', { at, need: 'alchemyConvert', more: [...(!valid ? ['수량을 다시 입력해 달라'] : count > can ? ['강화석이 부족하다'] : []), ...(S.alchemyBusy ? ['처리 중…'] : [])] })}</div>
      <div class="row">
        ${buyBtn(S.alchemyBusy ? '처리 중…' : '1개 전환', 'act.alchemyConvert(1)', { at, need: 'alchemyConvert', more: [...lack, ...(S.alchemyBusy ? ['처리 중…'] : [])] })}
        ${can >= 10 ? buyBtn(S.alchemyBusy ? '처리 중…' : '10개 전환', 'act.alchemyConvert(10)', { at, need: 'alchemyConvert', more: S.alchemyBusy ? ['처리 중…'] : [] }) : ''}
        ${can >= 1 ? buyBtn(S.alchemyBusy ? '처리 중…' : `전부 바꾸기 · ${num(can)}개`, 'act.alchemyConvert(-1)', { at, need: 'alchemyConvert', more: S.alchemyBusy ? ['처리 중…'] : [] }) : ''}
        ${can >= 1 ? `<span class="qt">지금 ${num(can)}개까지 만들 수 있다</span>` : ''}
      </div></div>`;
  }

  // ── ② 궁극 강화 ──────────────────────────────────────────────
  {
    const ults = eq.filter((e) => !e.crude && isUltimateEnhance(e.plus, e.grade ?? 0));
    let body;
    if (!ults.length) {
      body = muted(`궁극 등급 <b>+${MAX_PLUS}</b> 장비를 강화할 수 있다. 강화 상한은 없다.`);
    } else {
      body = ults.map((e) => {
        const need = blessedStoneCost(e.plus, e.grade ?? 0);
        const lack = bstones < need ? [`축복받은 강화석 ${need - bstones} 부족`] : [];
        return `<div class="item"><span class="nm">${eqName(e)} <span class="qt">+${e.plus}</span></span>
          ${buyBtn(`축복받은 강화석 ${need} · ${ULTIMATE_CHANCE * 100}%`, `act.bless('${e.id}')`, { at, need: 'bless', more: lack })}</div>`;
      }).join('');
    }
    ultimate = `<div class="sec"><h3>궁극 강화 <em>상한 없음</em></h3>${body}
      ${muted(`시도마다 <b>축복받은 강화석 ${ULTIMATE_BSTONES}개</b>가 든다. 성공하면 <b>1~3단계</b> 오르고,
        실패해도 장비는 부서지지 않는다. 행운·광고 배수도 이 <b>${ULTIMATE_CHANCE * 100}%</b>를 바꾸지 않는다.`)}</div>`;
  }

  // ── ③ 굴림 재추첨 ────────────────────────────────────────────
  list += `<div class="sec"><h3>굴림 재추첨 <em>1회 ${num(GAMBLE_PRICE)} 은화 · ${pool.length}개</em></h3>`;
  list += pool.length
    ? '<div class="grid">' + pool.map((e) => cell({
      key: 'a:' + e.id,
      ...gearTone(e), // F19 — 테두리 등급색
      glyph: gearGlyph(e.slot, e.crude ? null : e.grade || 0),
      badge: String(quality(e)),
      sel: S.alchemySel === e.id,
      title: eqTitle(e),
    })).join('') + '</div>'
    : muted('재추첨할 장비가 없다. 조잡한 장비와 고정 성능 장비는 재추첨할 수 없다.');
  list += '</div>';
  if (eq.some((e) => !e.crude && isUltimateEnhance(e.plus, e.grade ?? 0))) list += ultimate;
  list += `<details class="lore" id="alchemy-conversion"><summary>강화 재료 만들기 · 강화석 ${num(shards)} / 축복받은 강화석 ${num(bstones)}</summary><div class="in">${conversion}</div></details>`;

  list += lore('굴림이란', `
    같은 장비라도 <b>굴림 품질</b>(${ROLL_QUALITY_MIN}~${ROLL_QUALITY_MAX})에 따라 성능이 다르다.
    재추첨은 <b>굴림만</b> 다시 뽑는다 — 부위·등급·강화 수치·축복은 그대로다.<br>
    한 번 시도할 때마다 <b>${num(GAMBLE_PRICE)} 은화</b>가 들고, 목표까지 자동으로 반복한다.
    <b>새 품질은 즉시 적용되며, 이전보다 낮아져도 은화는 돌려받지 못한다.</b>`);

  // 상점의 연금술 탭은 상점이 이미 스크롤 뼈대를 소유하므로 중첩 split을 만들지 않는다.
  // 통합 상점은 selectedAction()을 자신의 하단 띠에 둔다. 스크롤 목록에 행동을 복제하지 않는다.
  return embedded
    ? (picked ? '' : idle()) + list
    : split(picked ? '' : idle(), list, picked ? focus(picked, { at }) : '');
}

/** 같은 선택과 같은 행동을 단독 창과 통합 상점의 하단 띠에서 함께 쓴다. */
export function selectedAction({ at, eq }) {
  const picked = rollable(eq).find((e) => e.id === S.alchemySel);
  return picked ? focus(picked, { at }) : '';
}

function idle() {
  return '<div class="focus idle">재추첨할 장비를 선택해 달라.</div>';
}

/** 고른 한 자루 — 지금 품질과 목표, 그리고 두 버튼. */
function focus(e, { at }) {
  const q = quality(e);
  const target = Math.max(ROLL_QUALITY_MIN, Math.min(ROLL_QUALITY_MAX, S.alchemyTarget ?? Math.min(ROLL_QUALITY_MAX, q + 5)));
  const done = q >= target;
  // 창고 은화는 패널을 연 직후 아직 로드되지 않았을 수 있다. 여기서
  // 지갑만 보고 막으면 실제 서버가 쓸 수 있는 창고 은화를 오판한다.
  const lack = S.alchemyBusy ? ['처리 중…'] : [];
  return `<div class="fname"><b>${eqName(e)}</b><span class="qt">품질 ${q} · 1회 ${num(GAMBLE_PRICE)} 은화</span></div>
    <div class="fnote"><b>새 품질은 즉시 적용 · 지금보다 낮아질 수 있다.</b> 부위·등급·강화는 그대로다.</div>
    <div class="ux-actions">
      ${buyBtn(S.alchemyBusy ? '처리 중…' : `한 번 굴리기 · ${num(GAMBLE_PRICE)} 은화`, `act.alchemyReroll('${e.id}')`, { y: S.you, at, need: 'alchemyReroll', price: GAMBLE_PRICE, more: lack })}
      <button class="ghost" onclick="act.alchemyClear()">${S.alchemyAuto ? '자동 중지' : '해제'}</button>
    </div>
    <details class="lore" id="alchemy-repeat"${S.alchemyAuto ? ' open' : ''}><summary>목표 품질까지 반복하기${S.alchemyAuto ? ' · 진행 중' : ''}</summary><div class="in">
    <label class="audio-row"><span>목표 ${target}</span>
      <input type="range" min="${ROLL_QUALITY_MIN}" max="${ROLL_QUALITY_MAX}" step="1" value="${target}"
        aria-label="목표 굴림 품질" oninput="act.alchemyTarget(this.value)">
      <output>${target}</output></label>
    <div class="row">
      ${done
        ? `<span class="qt">이미 목표 ${target} 이상이다</span>`
        : buyBtn(S.alchemyBusy ? '처리 중…' : `목표까지 자동 · 1회 ${num(GAMBLE_PRICE)} 은화`, `act.alchemyAutoReroll('${e.id}', ${target})`, { y: S.you, at, need: 'alchemyAutoReroll', price: GAMBLE_PRICE, more: lack })}
    </div>
    <div class="fnote">목표 품질에 도달하거나 은화가 부족하면 멈춘다. 시도마다 <b>${num(GAMBLE_PRICE)} 은화</b>가 든다. 자동 중지를 누르면 진행 중인 재추첨을 마친 뒤 멈춘다.</div></div></details>
    <div class="row"><button class="ghost" onclick="act.gearWork('${e.id}','inv')">가방에서 비교·착용</button>
    <button class="ghost" onclick="act.gearWork('${e.id}','forge')">이 장비 강화</button></div>`;
}
